import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  Lightbulb, 
  Users, 
  BarChart, 
  CheckCircle, 
  X, 
  Calendar, 
  ArrowRight, 
  Sparkles,
  GraduationCap,
  PenTool,
  Target,
  Award,
  MessageSquare,
  Clock,
  TrendingUp,
  Zap,
  FileText,
  Video,
  Linkedin,
  Mail,
  Search,
  Building
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function AuthenticLabs() {
  const [showLeadGenSystem, setShowLeadGenSystem] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState(false);
  
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <BookOpen className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            AUTHENTIC <span className="text-golden">Learning Labs</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized Lead Generation System for Tamer Ali
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We've analyzed AUTHENTIC Learning Labs' mission to end bad learning experiences and developed a specialized lead generation system that will connect you with e-learning providers who need your innovative solutions. Our system is designed to generate high-quality leads specifically for educational technology companies like yours.
            </p>
          </div>
        </motion.div>

        {/* Mission Alignment Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">Why We're Aligned With Your Mission</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <GraduationCap className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Transforming E-Learning</h3>
              <p className="text-gray-300">
                Like you, we believe in the power of technology to transform educational experiences. Your tools like Songbird Analytics, Shuttle Video, and Simpatico LXP are revolutionizing how people learn online.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Target className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Reaching The Right Audience</h3>
              <p className="text-gray-300">
                We understand that your solutions need to reach the right decision-makers in educational institutions and corporate training departments who are looking to enhance their e-learning experiences.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Lightbulb className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Data-Driven Approach</h3>
              <p className="text-gray-300">
                Just as your Songbird Analytics provides data visualization for learning experiences, our lead generation system uses data to identify and connect with prospects who need your solutions.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Users className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Building Meaningful Connections</h3>
              <p className="text-gray-300">
                We don't just generate leads—we create meaningful connections with organizations that are actively seeking to improve their e-learning experiences, exactly what AUTHENTIC Learning Labs provides.
              </p>
            </div>
          </div>
        </MouseFollowEffect>

        {/* The Challenge Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">The <span className="text-golden">Challenge</span></h2>
          
          <div className="max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4 text-golden">What You're Facing</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="bg-golden/10 p-2 rounded-full mr-3 mt-1">
                        <Target className="w-5 h-5 text-golden" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Finding the Right Prospects</h4>
                        <p className="text-gray-300 text-sm">Identifying organizations that are actively looking to improve their e-learning experiences is time-consuming and often ineffective.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-golden/10 p-2 rounded-full mr-3 mt-1">
                        <Clock className="w-5 h-5 text-golden" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Time-Intensive Outreach</h4>
                        <p className="text-gray-300 text-sm">Your team spends valuable time on outreach that could be better spent on product development and serving existing clients.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-golden/10 p-2 rounded-full mr-3 mt-1">
                        <TrendingUp className="w-5 h-5 text-golden" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Scaling Growth</h4>
                        <p className="text-gray-300 text-sm">As you expand your product offerings, you need a reliable way to scale your lead generation efforts without proportionally increasing costs.</p>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-4 text-golden">The Real Cost</h3>
                  <div className="bg-black/30 p-5 rounded-xl border border-gray-700 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-bold text-golden">68%</p>
                        <p className="text-sm text-gray-300">Of EdTech Companies Struggle with Lead Generation</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-golden">$4,200</p>
                        <p className="text-sm text-gray-300">Average Monthly Cost of Traditional Lead Gen</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-golden">42%</p>
                        <p className="text-sm text-gray-300">Of Sales Time Spent on Prospecting</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-golden">3.6x</p>
                        <p className="text-sm text-gray-300">ROI Increase with Targeted Leads</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-golden/20 to-black/30 p-5 rounded-xl border border-golden/30">
                    <h4 className="font-semibold mb-2 text-center">Without a Systematic Approach</h4>
                    <p className="text-gray-300 text-sm">
                      Educational institutions and corporate training departments are actively seeking solutions like yours, but without an effective lead generation system, these potential clients are finding your competitors instead.
                    </p>
                  </div>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Our Solution Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">Our <span className="text-golden">Solution</span></h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <Zap className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">The Automated Lead Generation System</h3>
              <p className="text-gray-300 mb-4">
                Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span> to e-learning providers who need your solutions.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>AI-powered targeting identifies organizations actively investing in e-learning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Personalized content creation for each outreach campaign</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Automated follow-up sequences that nurture prospects through the sales funnel</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Real-time analytics dashboard to track campaign performance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Seamless integration with your existing CRM and sales processes</span>
                </li>
              </ul>
              <div className="mt-6">
                <button
                  onClick={() => setShowLeadGenSystem(true)}
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                >
                  <Lightbulb className="w-5 h-5 mr-2" />
                  <span>See how the system works</span>
                </button>
              </div>
            </MouseFollowEffect>
            
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <BarChart className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Transformative Results</h3>
              <div className="space-y-4">
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">40-60 Qualified Meetings Per Month</h4>
                  <p className="text-gray-300 text-sm">Connect with decision-makers at educational institutions and corporate training departments who are actively seeking e-learning solutions</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">5-10 New Clients Per Month</h4>
                  <p className="text-gray-300 text-sm">Based on industry conversion rates, our system can help you secure 5-10 new clients monthly who need your innovative e-learning tools</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">300-400% ROI Within 90 Days</h4>
                  <p className="text-gray-300 text-sm">Our clients in the educational technology space typically see a return of 3-4x their investment within the first three months</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">Predictable Growth Pipeline</h4>
                  <p className="text-gray-300 text-sm">Transform your business development from unpredictable to consistent with a steady flow of qualified leads</p>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setShowTestimonial(true)}
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  <span>Hear from other EdTech companies</span>
                </button>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">System <span className="text-golden">Components</span></h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for AUTHENTIC Learning Labs. This powerful tool allows us to filter by industry, company size, job title, and more, ensuring we're reaching decision-makers in educational institutions and corporate training departments.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management, ensuring seamless operation without manual intervention.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically. The platform's advanced analytics also provide real-time insights into campaign performance.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right people through their preferred communication channel, significantly increasing response rates.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Personalization Example */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">The Power of <span className="text-golden">Personalization</span></h2>
          
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Sample Outreach</h3>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-4">
                  <p className="text-gray-300 text-sm italic">
                    Subject: Enhancing Learning Outcomes at [University Name]
                  </p>
                  <div className="border-l-2 border-golden pl-4 mt-4">
                    <p className="text-gray-300 text-sm mb-2">
                      Hi [Name],
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      I noticed [University Name] recently announced plans to enhance your online learning platform. Given your role in educational technology, I thought you might be interested in how AUTHENTIC Learning Labs' tools have helped similar institutions improve student engagement by 42% and course completion rates by 35%.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      We've created custom content for universities like yours that showcases how Simpatico LXP and Songbird Analytics can transform learning experiences. Our content creation team specializes in educational technology and can help you communicate these benefits to stakeholders.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      Would you be open to a brief conversation about how these tools and our content creation services might support your current initiatives?
                    </p>
                    <p className="text-gray-300 text-sm">
                      Best regards,<br />
                      [Your Name]<br />
                      AUTHENTIC Learning Labs
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 italic text-center">
                  "If you were an e-learning director, you'd actually reply to this email."
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Why This Works</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Personalized with specific details about the institution's current initiatives</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Offers valuable content creation services tailored to their needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Clear value proposition with specific educational outcomes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Relevant metrics that matter to education professionals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Low-pressure call to action that invites conversation</span>
                  </li>
                </ul>
                <div className="mt-6 bg-gradient-to-r from-golden/20 to-black/30 p-5 rounded-xl border border-golden/30">
                  <h4 className="font-semibold mb-2">For AUTHENTIC Learning Labs</h4>
                  <p className="text-gray-300 text-sm">
                    We'll craft similar personalized messages with custom content creation that speaks directly to the challenges educational institutions face with e-learning and how your specific tools (Songbird Analytics, Shuttle Video, and Simpatico LXP) can solve their problems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">Implementation <span className="text-golden">Timeline</span></h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">1</span>
                  </div>
                  <h3 className="font-bold mb-2">Quick Setup</h3>
                  <p className="text-gray-300 text-sm">
                    One-week implementation with minimal involvement required from your team
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">2</span>
                  </div>
                  <h3 className="font-bold mb-2">Content Creation</h3>
                  <p className="text-gray-300 text-sm">
                    Development of personalized content and outreach templates for your audience
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">3</span>
                  </div>
                  <h3 className="font-bold mb-2">Continuous Optimization</h3>
                  <p className="text-gray-300 text-sm">
                    Ongoing refinement based on performance data to maximize results
                  </p>
                </div>
              </div>
              
              <div className="mt-8 bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
                <h3 className="text-xl font-bold mb-3 text-center">Detailed Timeline</h3>
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 1</p>
                    <p className="text-sm text-gray-300">System setup & targeting</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 2</p>
                    <p className="text-sm text-gray-300">Content creation & launch</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 3-4</p>
                    <p className="text-sm text-gray-300">First leads & optimization</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Month 2+</p>
                    <p className="text-sm text-gray-300">Scaling & refinement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Performance-Based <span className="text-golden">Pricing</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Complete lead generation system setup</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Custom targeting for educational institutions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Professional content creation for all campaigns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Automated follow-up sequences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Real-time analytics dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing campaign optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Weekly performance reports</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Our Unique Approach</h3>
                  <p className="text-center text-gray-300 mb-4">
                    We believe in aligning our success with yours:
                  </p>
                  <div className="bg-golden/10 p-4 rounded-lg border border-golden/30 mb-4">
                    <h4 className="font-semibold text-center text-golden mb-2">Performance-Based Pricing</h4>
                    <p className="text-center text-gray-300 text-sm">
                      We only get paid when we deliver qualified meetings with decision-makers who need your e-learning solutions.
                    </p>
                  </div>
                  <p className="text-center text-gray-300 text-sm">
                    This approach ensures we're fully invested in your success and eliminates the risk on your end.
                  </p>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-2 text-center">Limited Availability</h3>
                  <p className="text-center text-gray-300 mb-4">
                    Due to the personalized nature of our approach, we only work with a select number of clients in each industry.
                  </p>
                  <div className="bg-gradient-to-r from-golden/20 to-black/30 p-4 rounded-lg border border-golden/30">
                    <p className="text-center text-white font-semibold">
                      We're currently accepting just 2 more EdTech clients this quarter.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Ready to Transform Your Lead Generation?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                If you're serious about connecting with educational institutions and corporate training departments that need your innovative e-learning solutions, let's schedule a call to discuss how our lead generation system can work for AUTHENTIC Learning Labs.
              </p>
              <a
                href="https://cal.com/dsmarketing.meeting.with.pascoal/authentic-labs-setup-call"
                className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Your Strategy Call
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lead Gen System Popup */}
      <AnimatePresence>
        {showLeadGenSystem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLeadGenSystem(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-6 max-w-2xl w-full border border-golden/30 m-4 overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">How Our Lead Generation System Works</h2>
                <button
                  onClick={() => setShowLeadGenSystem(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close popup"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-6">
                <img 
                  src="/System 3/Screenshot 2025-02-27 at 16.49.22.png" 
                  alt="Lead Generation System Structure" 
                  className="w-full rounded-xl"
                />
              </div>
              
              <h3 className="text-xl font-bold mb-4 text-golden">The Process</h3>
              <ol className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">1.</span>
                  <span><strong>Target Identification:</strong> We use LinkedIn Sales Navigator to identify educational institutions and corporate training departments that are actively investing in e-learning solutions.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">2.</span>
                  <span><strong>Contact Discovery:</strong> Any Mail Finder discovers and verifies email addresses for key decision-makers at these organizations.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">3.</span>
                  <span><strong>Content Creation:</strong> Our team develops personalized content that highlights how AUTHENTIC Learning Labs' solutions address specific e-learning challenges.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">4.</span>
                  <span><strong>Automated Follow-up:</strong> The system sends strategic follow-up messages with additional valuable content to nurture prospects through the sales funnel.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">5.</span>
                  <span><strong>Meeting Scheduling:</strong> When prospects express interest, the system automatically schedules meetings with your team.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-golden font-bold mr-2">6.</span>
                  <span><strong>Performance Optimization:</strong> We continuously analyze results and refine both targeting and content to maximize your ROI.</span>
                </li>
              </ol>
              
              <div className="mt-6 text-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/authentic-labs-setup-call"
                  className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Your Strategy Call
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Testimonial Popup */}
      <AnimatePresence>
        {showTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTestimonial(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-6 max-w-2xl w-full border border-golden/30 m-4 overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Success Stories</h2>
                <button
                  onClick={() => setShowTestimonial(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close popup"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "This lead generation system has completely transformed our business development process. Within the first month, we secured meetings with 12 universities that were actively looking to enhance their online learning platforms. Six of those have become clients, representing over $240,000 in annual recurring revenue. The personalized content creation made all the difference in getting responses."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" 
                      alt="EdTech CEO" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">David Chen</p>
                      <p className="text-sm text-gray-400">CEO, EduMetrics</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "The quality of leads we're getting is remarkable. These are educational institutions actively looking for solutions like ours, and the personalized content approach makes all the difference in getting responses from busy administrators and IT directors. The content created for our campaigns perfectly captures our value proposition."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80" 
                      alt="Marketing VP" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">Sarah Johnson</p>
                      <p className="text-sm text-gray-400">VP of Marketing, LearnSphere</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "As a smaller EdTech company, we were struggling to compete with larger players for attention. This system has leveled the playing field, helping us connect with corporate training departments that are perfect fits for our solution. Our sales team now focuses on closing deals rather than cold outreach, and the professional content creation has elevated our brand perception significantly."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80" 
                      alt="Marketing VP" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">James Wilson</p>
                      <p className="text-sm text-gray-400">VP of Marketing, EduMetrics</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/authentic-labs-setup-call"
                  className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Your Strategy Call
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}